export enum TimeEntrySource {
    MANUAL = 'MANUAL',
    AUTO = 'AUTO',
    ADMIN = 'ADMIN',
}

export interface TimeLog {
  id: string;
  employeeId: number;
  activityId: string;
  startTime: number;
  endTime?: number;
  source: TimeEntrySource;
  createdAt: number;
}

export enum EmployeeStatus {
  CLOCKED_OUT = 'CLOCKED_OUT',
  CLOCKED_IN = 'CLOCKED_IN',
  ON_BREAK = 'ON_BREAK',
}

export enum PayrollFrequency {
    WEEKLY = 'Weekly',
    BIWEEKLY = 'BiWeekly',
    SEMI_MONTHLY = 'Semi-Monthly',
    MONTHLY = 'Monthly',
    EMPLOYEE_DEFINED = 'Employee Defined',
}

export interface PayrollSettings {
    frequency: PayrollFrequency;
    startDate: string;
    endDate?: string;
    isStartDateLocked?: boolean;
}

export enum EmployeeRole {
    EMPLOYEE = 'EMPLOYEE',
    ADMIN = 'ADMIN',
    INACTIVE = 'INACTIVE',
}

export interface LeaveBalance {
  leaveTypeId: string;
  leaveTypeName: string;
  entitled: number;
  used: number;
  available: number;
  fromDate?: number;
  toDate?: number;
}

export interface Employee {
  id: string;
  emp_number: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  pin: string;
  status: EmployeeStatus;
  createdAt: number;
  role?: EmployeeRole;
  streetAddress?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  telephone?: string;
  cellPhone?: string;
  email?: string;
  sin?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  notes?: string;
  hireDate?: string;
  employmentType?: 'Full Time' | 'Part Time';
  employeeNumber?: string;
  leaveBalances?: LeaveBalance[];
}

export interface TimesheetEntry {
  date: string;
  employeeId: string;
  employeeName: string;
  clockInTime?: number;
  clockOutTime?: number;
  breakDuration: number;
  totalWorkDuration: number;
}

export interface PayrollEntry {
    employeeId: string;
    employeeName: string;
    totalHours: number;
}

export interface EditableTimeSegment {
  id: string;
  timeLogId?: string;
  leaveEntryId?: string;
  
  employeeId: string;
  employeeName:string;
  
  activity: string;
  activityId: string;

  startTime: number;
  endTime?: number;
  
  startDate: string;
  ui_startTime: string;
  stopDate: string;
  ui_stopTime: string;
  
  hours: number;
  source: 'time' | 'leave';
}

export enum LeaveType {
    VACATION = 'Vacation',
    SICK = 'Sick',
    PERSONAL = 'Personal',
    UNPAID = 'Unpaid',
}

export enum LeaveCategoryCalculation {
    PAID = 'PAID',
    UNPAID = 'UNPAID',
}

export interface LeaveCategory {
    id: string;
    name: string;
    calculation: LeaveCategoryCalculation;
    source?: 'kiosk' | 'orangehrm';
}

export interface LeaveEntry {
    id: string;
    employeeIds: string[];
    leaveCategoryId: string;
    startDate: number;
    endDate: number;
    days: number;
    hoursPerDay: number;
    createdAt: number;
    skipSaturday?: boolean;
    skipSunday?: boolean;
}

export enum TimeDisplayFormat {
    DECIMAL = 'DECIMAL',
    MINUTES = 'MINUTES',
}

export interface FilterSettings {
    employeeIds: string[];
    startDate: string;
    endDate: string;
    timeDisplayFormat: TimeDisplayFormat;
}

export interface ReportSegment {
    day: string;
    date: string;
    startTime: string;
    stopTime: string;
    activity: string;
    unpaid: number;
    regular: number;
    ot: number;
}

export interface ReportDay {
    date: Date;
    segments: ReportSegment[];
    totalPaid: number;
}

export interface ReportWeek {
    weekEnding: string;
    days: ReportDay[];
    totalRegular: number;
    totalOT: number;
    totalPaid: number;
}

export interface ActivitySummary {
    unpaid: number;
    regular: number;
    ot: number;
    paid: number;
}

export interface TimecardDetailData {
    employeeNumber: number;
    employeeName: string;
	generatedAt: string;
    dateRange: string;
    weeks: ReportWeek[];
    grandTotals: {
        regular: number;
        ot: number;
        paid: number;
    };
    activitySummary: Record<string, ActivitySummary>;
    leaveSummary: Record<string, ActivitySummary>;
}

export interface EmployeeSummary {
    employeeId: string;
    employeeNumber: number;
    employeeName: string;
    unpaid: number;
    regular: number;
    ot: number;
    totalPaid: number;
}

export interface TimecardSummaryData {
    dateRange: string;
    generatedAt: string;
    employeeSummaries: EmployeeSummary[];
    totals: {
        unpaid: number;
        regular: number;
        ot: number;
        totalPaid: number;
    }
}

export enum DayOfWeek {
    Sunday = 'Sunday',
    Monday = 'Monday',
    Tuesday = 'Tuesday',
    Wednesday = 'Wednesday',
    Thursday = 'Thursday',
    Friday = 'Friday',
    Saturday = 'Saturday',
}

export interface RateCondition {
    enabled: boolean;
    threshold: number;
}

export interface RateDefinition {
    rate: number;
    weekly: RateCondition;
    daily: Record<DayOfWeek, RateCondition>;
}

export interface OvertimeRule {
    id: string;
    name: string;
    assignedEmployeeIds: string[];
    rates: [RateDefinition, RateDefinition];
}

export enum ActivityCalculation {
    PAID = 'PAID',
    UNPAID = 'UNPAID',
}

export interface Activity {
    id: string;
    name: string;
    calculation: ActivityCalculation;
}

export enum GroupInterface {
    GRID = 'Grid',
    IN_OUT_BOARD = 'In/Out Board',
    PIN_ONLY = 'PIN Only',
}

export enum NameDisplayFormat {
    FIRST_LAST = 'First Last',
    LAST_FIRST = 'Last, First',
}

export enum InOutBoardDisplay {
    ONE_LIST = 'One list',
}

export enum InOutBoardSelection {
    EMPLOYEE_DASHBOARD = 'Employee Dashboard',
}

export interface DisplayGroupSettings {
    groupInterface: GroupInterface;
    password?: string;
    options: {
        requireEmployeePINs: boolean;
    };
    nameFormat?: NameDisplayFormat;
    shiftId: string | null;
    breaksAndLunches: {
        showBreakButton: boolean;
        showLunchButton: boolean;
    };
    messaging: {
        enableMailForEmployees: boolean;
        enableMailForManagers: boolean;
    };
    inOutBoardOptions: {
        display: InOutBoardDisplay;
        columns: {
            status: boolean;
            date: boolean;
            time: boolean;
            elapsed: boolean;
            messages: boolean;
        };
        selection: InOutBoardSelection;
    };
}

export interface DisplayGroup {
    id: string;
    name: string;
    employeeIds: string[];
    managerIds: string[];
    settings: DisplayGroupSettings;
    isDefault?: boolean;
}

export enum TimeRounding {
    NONE = 'NONE',
    TENTH_HOUR = 'TENTH_HOUR',
    QUARTER_HOUR = 'QUARTER_HOUR',
}

export enum PinpadSize {
    HIDDEN = 'Hidden',
    SMALL = 'Small',
    MEDIUM = 'Medium',
    LARGE = 'Large',
}

export enum TimestampSource {
    DEVICE = 'DEVICE',
}

export interface TimeSettings {
    timestampSource: TimestampSource;
    workweekStartsWith: DayOfWeek;
    includePaidLeaveInOT: boolean;
    includePastMidnight: boolean;
    rounding: TimeRounding;
}

export interface ClockOutRules {
    autoClockOut: {
        enabled: boolean;
        minutes: number;
    };
}

export interface Shift {
    id: string;
    name: string;
    startTime: string;
    stopTime: string;
    clockOutRules: ClockOutRules;
}

export type EmployeeSchedule = (string | null)[];

export type Schedules = Record<string, EmployeeSchedule>;

export interface CompanySettings {
    companyName: string;
    companyAddress: string;
    isSetupComplete?: boolean;
}

export enum WeatherUnits {
    IMPERIAL = 'imperial',
    METRIC = 'metric',
}

export interface ScreensaverSettings {
    enabled: boolean;
    timeoutMinutes: number;
    widgets: {
        time: boolean;
        date: boolean;
        weather: boolean;
    };
    latitude: number;
    longitude: number;
    weatherUnits: WeatherUnits;
}

export interface SMTPSettings {
    host: string;
    port: number;
    username: string;
    password?: string;
    fromAddress: string;
    encryption: 'none' | 'ssl' | 'tls';
}

export interface NotificationSettings {
    notifyOnMissedPunch: boolean;
    ccManagers?: boolean;
    adminIdsToNotify?: string[];
}

export interface WeatherData {
    temp: number;
    description: string;
    icon: string;
    feelsLike: number;
    windSpeed: number;
    humidity: number;
    pressure: number;
    precipitation: number;
    timestamp: number;
}